import { Link } from 'react-router-dom';
import { ROUTE_NAMES } from '@tsgecart/config';
import { useCategories } from '../../catalog/hooks/use-catalog.js';

export interface CategoryNavProps {
  /** Desktop renders an inline row of links; mobile renders a stacked list inside the drawer. */
  variant?: 'desktop' | 'mobile';
  onNavigate?: () => void;
}

/**
 * "Shop by category" navigation built from `useCategories()`, top-level
 * only (`parentId === null`). Fails silently on error/empty rather than
 * breaking the header -- category browsing is a convenience, not
 * essential chrome.
 */
export function CategoryNav({ variant = 'desktop', onNavigate }: CategoryNavProps) {
  const { data, isLoading, isError } = useCategories();
  const topLevel = (data ?? [])
    .filter((category) => category.parentId === null)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  if (isError) return null;

  if (variant === 'mobile') {
    if (isLoading) {
      return (
        <div className="flex flex-col gap-2 px-1 py-2" aria-hidden="true">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="h-4 w-32 animate-pulse rounded bg-gray-100" />
          ))}
        </div>
      );
    }
    if (topLevel.length === 0) return null;
    return (
      <nav aria-label="Shop by category" className="flex flex-col gap-1">
        {topLevel.map((category) => (
          <Link
            key={category.id}
            to={`${ROUTE_NAMES.category}/${category.slug}`}
            onClick={onNavigate}
            className="rounded-lg px-2 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {category.name}
          </Link>
        ))}
      </nav>
    );
  }

  if (isLoading) {
    return (
      <div className="hidden items-center gap-4 border-b border-gray-100 px-8 py-2 lg:flex" aria-hidden="true">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="h-3.5 w-16 animate-pulse rounded bg-gray-100" />
        ))}
      </div>
    );
  }

  if (topLevel.length === 0) return null;

  return (
    <nav
      aria-label="Shop by category"
      className="hidden items-center gap-6 overflow-x-auto border-b border-gray-100 px-8 py-2 text-sm lg:flex"
    >
      {topLevel.map((category) => (
        <Link
          key={category.id}
          to={`${ROUTE_NAMES.category}/${category.slug}`}
          className="hover:text-brand-foreground whitespace-nowrap font-medium text-gray-600"
        >
          {category.name}
        </Link>
      ))}
    </nav>
  );
}
